import { useEffect, useState, useMemo } from "react";
import BarChart from "./BarChart";
import {
  CHART_BG_COLORS,
  CHART_BORDER_COLORS,
  check_date_within_range,
} from "../../utils/reports";
import {
  IdentifiedObjectItem,
  IdentifiedSpeciesCount,
} from "../../models/Media";
import { CameraItem } from "../../models/Camera";

interface SpeciesBySeasonProps {
  cameras: CameraItem[];
  selectedCameras: CameraItem[];
  allIdentifiedObjects: IdentifiedObjectItem[];
  selectedIdentifiedObjects: IdentifiedObjectItem[];
  reportData: IdentifiedSpeciesCount[];
  startDate: string;
  endDate: string;
}

export const seasons = [
  { name: "Summer", startMonth: 0, endMonth: 2 },
  { name: "Autumn", startMonth: 3, endMonth: 5 },
  { name: "Winter", startMonth: 6, endMonth: 8 },
  { name: "Spring", startMonth: 9, endMonth: 11 },
];

// Memoized function to preprocess reportData for fast lookups
export const useProcessedReportData = (
  reportData: IdentifiedSpeciesCount[],
  startDate: string,
  endDate: string
) => {
  return useMemo(() => {
    const map = new Map<string, IdentifiedSpeciesCount[]>();
    reportData.forEach((data) => {
      if (!check_date_within_range(startDate, endDate, data.date)) return;
      const key = `${data.identified_object_name}-${data.camera}`;
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key)?.push(data);
    });
    return map;
  }, [reportData, startDate, endDate]);
};

// Optimized function to get count per season for a specific camera
export const getCount = (
  objectName: string,
  seasonIndex: number,
  cameraId: string,
  dataMap: Map<string, IdentifiedSpeciesCount[]>
) => {
  const key = `${objectName}-${cameraId}`;
  const matchingData = dataMap.get(key) || [];
  return matchingData.reduce((total, data) => {
    const dataDate = new Date(data.date);
    const isWithinSeason =
      dataDate.getMonth() >= seasons[seasonIndex].startMonth &&
      dataDate.getMonth() <= seasons[seasonIndex].endMonth;
    return isWithinSeason ? total + 1 : total;
  }, 0);
};

// Optimized function to get total count across all cameras for a given season
export const getTotalCount = (
  objectName: string,
  seasonIndex: number,
  selectedCameras: CameraItem[],
  dataMap: Map<string, IdentifiedSpeciesCount[]>
) => {
  return selectedCameras.reduce(
    (total, camera) =>
      total + getCount(objectName, seasonIndex, camera.camera_id, dataMap),
    0
  );
};

const SpeciesBySeasons = ({
  cameras,
  selectedCameras,
  allIdentifiedObjects,
  selectedIdentifiedObjects,
  reportData,
  startDate,
  endDate,
}: SpeciesBySeasonProps) => {
  const [mapData, setMapData] = useState<any>(null);
  const [csvData, setCsvData] = useState<any>();

  const dataMap = useProcessedReportData(reportData, startDate, endDate);

  const csvArrays = useMemo(() => {
    return (seasonName: string, seasonIndex: number) =>
      selectedCameras.flatMap((camera) =>
        selectedIdentifiedObjects.map((object) => ({
          Camera: camera.camera_id,
          Object: object.name,
          Season: seasonName,
          Count: getCount(object.name, seasonIndex, camera.camera_id, dataMap),
        }))
      );
  }, [selectedCameras, selectedIdentifiedObjects, dataMap]);

  // Generate CSV Data
  useEffect(() => {
    const csv = seasons.flatMap((seasonName, seasonIndex) =>
      csvArrays(seasonName.name, seasonIndex)
    );
    setCsvData(csv);
  }, [csvArrays]);

  // Generate Chart Data
  useEffect(() => {
    if (selectedCameras.length && selectedIdentifiedObjects.length) {
      const data = {
        labels: seasons.map((season) => season.name),
        datasets: selectedIdentifiedObjects.map((object, index) => ({
          label: object.name,
          data: seasons.map((_, seasonIndex) =>
            getTotalCount(object.name, seasonIndex, selectedCameras, dataMap)
          ),
          backgroundColor: CHART_BG_COLORS[Object.keys(CHART_BG_COLORS)[index]],
          borderColor:
            CHART_BORDER_COLORS[Object.keys(CHART_BORDER_COLORS)[index]],
          borderWidth: 1,
        })),
      };
      setMapData(data);
    }
  }, [selectedCameras, selectedIdentifiedObjects, dataMap]);

  // Optimize Y-Axis Scaling
  const getYAxisConfig = (datasets: any[]) => {
    const allValues = datasets.flatMap((dataset) => dataset.data || []);
    const maxValue = Math.max(...allValues, 10);
    const stepSize = maxValue < 10 ? 1 : maxValue < 70 ? 10 : 50;
    return {
      suggestedMax: Math.ceil(maxValue / stepSize) * stepSize,
      stepSize,
    };
  };

  const { suggestedMax, stepSize } = mapData
    ? getYAxisConfig(mapData.datasets)
    : { suggestedMax: 10, stepSize: 1 };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" as const },
      title: { display: true, text: "Animals Observed by Season" },
    },
    layout: { padding: { left: 0, right: 10, top: 0, bottom: 0 } },
    scales: {
      y: { min: 0, suggestedMax, ticks: { stepSize } },
    },
  };

  return (
    <BarChart
      mapData={mapData}
      options={options}
      csvData={csvData}
      name="Species Observed by Seasons"
    />
  );
};

export default SpeciesBySeasons;
